import { defineStore } from "pinia";
import { isProductMappingStatus } from "@price-rescue/api-contracts";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultManualMappingDraft,
  defaultMappingDecisionDraft,
  defaultMappingFilters,
  validateDecisionDraft,
  validateManualMappingDraft,
  type ManualMappingDraft,
  type MappingConflictState,
  type MappingDecisionDraft,
  type MappingFilters,
  type MappingStatus,
  type ProductMapping,
} from "./models.js";
import type { MappingsService } from "./service.js";

export const useMappingsStore = defineStore("merchant-mappings", {
  state: (): {
    conflict?: MappingConflictState;
    createDraft: ManualMappingDraft;
    decisionDrafts: Record<string, MappingDecisionDraft>;
    detail?: ProductMapping;
    detailError?: string;
    filters: MappingFilters;
    items: ProductMapping[];
    listError?: string;
    loadingDetail: boolean;
    loadingList: boolean;
    mutationLoading: boolean;
    mutationMessage?: string;
    nextCursor: string | null;
    service?: MappingsService;
    validationErrors: string[];
  } => ({
    createDraft: defaultManualMappingDraft(),
    decisionDrafts: {},
    filters: defaultMappingFilters(),
    items: [],
    loadingDetail: false,
    loadingList: false,
    mutationLoading: false,
    nextCursor: null,
    validationErrors: [],
  }),
  actions: {
    configure(service: MappingsService): void {
      this.service = service;
    },
    async loadList(filters: MappingFilters): Promise<void> {
      this.filters = filters;
      this.loadingList = true;
      delete this.listError;
      try {
        const page = await this.requireService().loadMappings(filters);
        this.items = page.mappings;
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        this.listError = messageForError(error, "Mappings could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadMore(): Promise<void> {
      if (this.nextCursor === null) return;
      this.loadingList = true;
      delete this.listError;
      try {
        const page = await this.requireService().loadMappings({
          ...this.filters,
          cursor: this.nextCursor,
        });
        this.items = [...this.items, ...page.mappings];
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        this.listError = messageForError(error, "More mappings could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadDetail(productId: string, mappingId: string): Promise<void> {
      this.loadingDetail = true;
      delete this.detailError;
      try {
        this.detail = await this.requireService().loadMapping(productId, mappingId);
        this.ensureDecisionDraft(this.detail);
      } catch (error: unknown) {
        this.detailError = messageForError(error, "Mapping detail could not be loaded.");
      } finally {
        this.loadingDetail = false;
      }
    },
    resetCreateDraft(): void {
      this.createDraft = defaultManualMappingDraft();
      this.validationErrors = [];
      delete this.conflict;
      delete this.detailError;
      delete this.mutationMessage;
    },
    clearDetail(): void {
      delete this.detail;
      delete this.conflict;
      delete this.detailError;
      delete this.mutationMessage;
      this.validationErrors = [];
    },
    async createMapping(): Promise<void> {
      this.validationErrors = validateManualMappingDraft(this.createDraft);
      if (this.validationErrors.length > 0) return;
      const created = await this.runMappingMutation(
        () => this.requireService().createMapping(this.createDraft),
        "Manual mapping created.",
      );
      if (created) {
        this.createDraft = defaultManualMappingDraft();
      }
    },
    async submitDecision(mapping: ProductMapping): Promise<boolean> {
      const draft = this.decisionDrafts[mapping.mappingId] ?? defaultMappingDecisionDraft(mapping);
      this.validationErrors = validateDecisionDraft(draft, mapping);
      if (this.validationErrors.length > 0) return false;
      return await this.runMappingMutation(
        () => this.requireService().patchMapping(mapping, draft),
        "Mapping updated.",
      );
    },
    async disableMapping(mapping: ProductMapping): Promise<void> {
      this.decisionDrafts[mapping.mappingId] = {
        ...defaultMappingDecisionDraft(mapping),
        action: "DISABLE",
        reason: "MERCHANT_DISABLED_MAPPING",
      };
      const updated = await this.submitDecision(mapping);
      if (!updated) return;
      this.items = this.items.filter((item) => item.mappingId !== mapping.mappingId);
      if (this.detail?.mappingId === mapping.mappingId) {
        delete this.detail;
      }
    },
    ensureDecisionDraft(mapping: ProductMapping): void {
      this.decisionDrafts[mapping.mappingId] ??= defaultMappingDecisionDraft(mapping);
    },
    keepDraftAfterConflict(): void {
      if (this.conflict === undefined) return;
      if (this.conflict.mappingId !== undefined && this.conflict.preservedDraft !== undefined) {
        this.decisionDrafts[this.conflict.mappingId] = this.conflict.preservedDraft;
      }
      delete this.conflict;
    },
    async runMappingMutation(
      operation: () => Promise<ProductMapping>,
      successMessage: string,
    ): Promise<boolean> {
      delete this.conflict;
      delete this.detailError;
      delete this.mutationMessage;
      this.mutationLoading = true;
      try {
        const mapping = await operation();
        this.applyMapping(mapping);
        this.detail = mapping;
        this.ensureDecisionDraft(mapping);
        this.mutationMessage = successMessage;
        return true;
      } catch (error: unknown) {
        this.applyMutationError(error);
        return false;
      } finally {
        this.mutationLoading = false;
      }
    },
    applyMapping(mapping: ProductMapping): void {
      const exists = this.items.some((item) => item.mappingId === mapping.mappingId);
      this.items = exists
        ? this.items.map((item) => (item.mappingId === mapping.mappingId ? mapping : item))
        : [mapping, ...this.items];
    },
    applyMutationError(error: unknown): void {
      if (isMappingConflictError(error)) {
        const duplicate = duplicateDetailsFrom(error.details);
        const mappingId = this.detail?.mappingId ?? duplicate?.existingMappingId;
        this.conflict = {
          ...(mappingId === undefined ? {} : { mappingId }),
          message: messageForError(error, "Mapping change could not be saved."),
          ...(this.detail === undefined
            ? {}
            : {
                preservedDraft:
                  this.decisionDrafts[this.detail.mappingId] ??
                  defaultMappingDecisionDraft(this.detail),
              }),
          ...(duplicate === undefined ? {} : { duplicate }),
        };
      }
      this.detailError = messageForError(error, "Mapping change could not be saved.");
    },
    requireService(): MappingsService {
      if (this.service === undefined) {
        throw new Error("Mappings store was not configured before use.");
      }
      return this.service;
    },
  },
});

export { defaultMappingDecisionDraft, defaultMappingFilters };

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.code === "REQUEST_DUPLICATE")
    return "An active mapping already exists for this product variant and competitor URL.";
  if (error instanceof MerchantApiError && error.kind === "conflict")
    return "This mapping changed while you were editing. Your review draft is preserved.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function isMappingConflictError(error: unknown): error is MerchantApiError {
  return (
    error instanceof MerchantApiError &&
    (error.kind === "conflict" || error.code === "REQUEST_DUPLICATE")
  );
}

function duplicateDetailsFrom(
  details: Record<string, unknown> | null | undefined,
): MappingConflictState["duplicate"] | undefined {
  if (details?.["state"] !== "DUPLICATE_ACTIVE_MAPPING") {
    return undefined;
  }

  const existingMappingId = stringDetail(details, "existingMappingId");
  const existingStatus = mappingStatusDetail(details, "existingStatus");
  const platformProductId = stringDetail(details, "platformProductId");
  const platformVariantId = stringDetail(details, "platformVariantId");
  const canonicalUrl = stringDetail(details, "canonicalUrl");

  return {
    ...(existingMappingId === undefined ? {} : { existingMappingId }),
    ...(existingStatus === undefined ? {} : { existingStatus }),
    ...(platformProductId === undefined ? {} : { platformProductId }),
    ...(platformVariantId === undefined ? {} : { platformVariantId }),
    ...(canonicalUrl === undefined ? {} : { canonicalUrl }),
  };
}

function mappingStatusDetail(
  details: Record<string, unknown>,
  key: string,
): MappingStatus | undefined {
  const value = stringDetail(details, key);
  return value !== undefined && isProductMappingStatus(value) ? value : undefined;
}

function stringDetail(details: Record<string, unknown>, key: string): string | undefined {
  const value = details[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
